import { Component, computed, inject, input, OnChanges, OnDestroy, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { faCodeBranch } from '@fortawesome/pro-regular-svg-icons';

import { plainToInstance } from 'class-transformer';
import { Subscription } from 'rxjs';

import { AddCondition, BlockModel } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { QuestionEditor } from '../../../models/question-editor.model';
import { BlockTypeContainers } from '../../../models/survey-editor/block-type.constants';
import { SurveyConditionalsService } from '../../../services/survey-conditionals.service';
import { SurveyEditorService } from '../../../services/survey-editor.service';

@Component({
  selector: 'empathy-conditional-blocks-logic',
  templateUrl: 'conditional-blocks-logic.component.html',
  styleUrls: ['./conditional-blocks-logic.component.scss']
})
export class ConditionalBlocksLogicComponent implements OnChanges, OnDestroy {
  activeBlock = input<FormGroup>();

  readonly emptyBlockMessage$ = computed(() =>
    BlockTypeContainers.includes(this.activeBlock()?.value.type)
      ? 'Add a question to any child block to configure parent conditions.'
      : 'To start adding conditions, the block must have at least one question.'
  );

  blockNotAllowConditions$ = signal<boolean>(false);
  previousQuestions$ = signal<Question[]>([]);
  isEditing$ = signal<boolean>(false);

  faCodeBranch = faCodeBranch;

  surveyEditorService = inject(SurveyEditorService);

  private subscription: Subscription;
  private surveyConditionalsService = inject(SurveyConditionalsService);

  ngOnChanges(): void {
    if (!this.activeBlock()) {
      return;
    }

    this.refreshOnBlockChanges(this.mapInitialRefresh());

    this.subscription = this.surveyEditorService.surveyEditor$().blocks.change$.subscribe((data) => {
      this.refreshOnBlockChanges(data);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onSave(newCondition: AddCondition): void {
    this.surveyEditorService.surveyEditor$().blocks.addConditionToActiveBlock(newCondition);

    this.isEditing$.set(false);
    this.surveyConditionalsService.conditionForm$().reset();
    this.surveyConditionalsService.questionSelected$.set(null);
  }

  onRemoveCondition(index: number): void {
    this.surveyConditionalsService.removeConditionFromActiveBlock(index);
  }

  private refreshOnBlockChanges(dataChanged: { blocks: BlockModel[]; parentBlock?: BlockModel }): void {
    setTimeout(() => {
      this.isEditing$.set(false);
      this.previousQuestions$.set([...this.mapSourceQuestions(), ...this.surveyEditorService.surveyEditor$().previousConditionSelectableQuestionsForBlock()]);

      this.blockNotAllowConditions$.set(!dataChanged.blocks.some((block) => (block?.groupedQuestionIds?.length ?? 0) > 0));
    });
  }

  private mapInitialRefresh(): { blocks: BlockModel[]; parentBlock?: BlockModel } {
    const activeBlock = this.activeBlock().value;
    const isParent = BlockTypeContainers.includes(activeBlock.type);
    return {
      parentBlock: isParent ? activeBlock : null,
      blocks: isParent
        ? activeBlock.groupedBlockIds.map((qId: string) =>
            this.surveyEditorService
              .surveyEditor$()
              .blocks.all$()
              .find((b) => b.id === qId)
          )
        : [activeBlock]
    };
  }

  private mapSourceQuestions(): QuestionEditor[] {
    return plainToInstance(
      QuestionEditor,
      this.surveyEditorService
        .surveyEditor$()
        .sources.all.flatMap((sourceCtrl) => sourceCtrl.value.variables as QuestionEditor[])
        .filter((question) => question.variableRequiredType !== 'UNIQUE' && !!question.variableToken),
      { enableImplicitConversion: true }
    );
  }
}
